import { ConfiguredEOASchool, SchoolConfigEntry } from '../../interfaces/EOAInterface';
import { decimalNumberError } from './fieldValidation';
import createFormRowId from './formRowId';
import { ensureDraftId } from './persistNestedConfiguration';

export type YesNoAnswer = 'yes' | 'no' | '';

export interface SchoolInfoRow {
  id: string;
  savedId: string | null;
  name: string;
  nameConfirmed: boolean;
  isPrimary: YesNoAnswer;
  schoolNumber: string;
  schoolType: string;
  graduationStatus: YesNoAnswer;
  requiresSat: YesNoAnswer;
  requiresAct: YesNoAnswer;
  hasCteCourses: YesNoAnswer;
  hasDetailedCteClusters: YesNoAnswer;
  highWageThreshold: string;
  highGrowthThreshold: string;
  regionalAreaType: string;
  regionalAreaTitle: string;
}

export interface SchoolInfoForm {
  schools: SchoolInfoRow[];
}

export interface SchoolInfoWriteBody {
  name?: string;
  type?: string;
  src_school_id?: string;
  is_primary_on_eoa?: boolean;
  test_types?: string[];
  regional_area_type?: string;
  regional_area_title?: string;
  career_high_wage_threshold?: number;
  career_high_growth_threshold?: number;
  has_cte_courses?: boolean;
  required_act_for_graduation?: boolean;
  required_sat_for_graduation?: boolean;
  can_provide_graduation_status?: boolean;
  has_detailed_cte_clusters?: boolean;
}

type SchoolInfoPatchBody = { [K in keyof SchoolInfoWriteBody]?: SchoolInfoWriteBody[K] | null };

export interface RegionalAreaOption {
  label: string;
  value: string;
}

const REGIONAL_AREA_OPTIONS: RegionalAreaOption[] = [
  { label: 'National', value: 'NATIONAL' },
  { label: 'State', value: 'STATE' },
  { label: 'US Territory', value: 'US_TERRITORY' },
  { label: 'Metropolitan statistical area', value: 'METROPOLITAN_STATISTICAL_AREA' },
  { label: 'Nonmetropolitan statistical area', value: 'NONMETROPOLITAN_STATISTICAL_AREA' },
];

const emptySchool = (): SchoolInfoRow => ({
  id: createFormRowId('school'),
  savedId: null,
  name: '',
  nameConfirmed: false,
  isPrimary: '',
  schoolNumber: '',
  schoolType: '',
  graduationStatus: '',
  requiresSat: '',
  requiresAct: '',
  hasCteCourses: '',
  hasDetailedCteClusters: '',
  highWageThreshold: '',
  highGrowthThreshold: '',
  regionalAreaType: '',
  regionalAreaTitle: '',
});

const INITIAL_SCHOOL_INFO_FORM: SchoolInfoForm = {
  schools: [],
};

const yesNoFromBoolean = (value: boolean | undefined): YesNoAnswer => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
};

const booleanFromYesNo = (value: YesNoAnswer): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
};

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed || decimalNumberError(trimmed) !== null) return undefined;
  return Number(trimmed);
};

const numberToField = (value: number | undefined | null): string =>
  value === undefined || value === null ? '' : String(value);

const showsDetailedCte = (school: SchoolInfoRow): boolean => school.hasCteCourses === 'yes';

const showsWageThresholds = (school: SchoolInfoRow): boolean =>
  showsDetailedCte(school) && school.hasDetailedCteClusters === 'yes';

const SCHOOL_NAME_REQUIRED_ERROR = 'Enter a school name.';

const schoolNameError = (name: string): string | null => (name.trim() ? null : SCHOOL_NAME_REQUIRED_ERROR);

const schoolWageError = (school: SchoolInfoRow): boolean => {
  if (!showsWageThresholds(school)) return false;
  return (
    decimalNumberError(school.highWageThreshold) !== null || decimalNumberError(school.highGrowthThreshold) !== null
  );
};

const hasInvalidSchoolInfo = (form: SchoolInfoForm): boolean =>
  form.schools.some((school) => schoolNameError(school.name) !== null || schoolWageError(school));

const hasSchoolContent = (school: SchoolInfoRow): boolean =>
  Boolean(
    (school.nameConfirmed && school.name.trim()) ||
      school.isPrimary ||
      school.schoolNumber.trim() ||
      school.schoolType.trim() ||
      school.graduationStatus ||
      school.requiresSat ||
      school.requiresAct ||
      school.hasCteCourses ||
      school.regionalAreaType ||
      school.regionalAreaTitle.trim()
  );

const addSchool = (form: SchoolInfoForm): SchoolInfoForm => ({
  ...form,
  schools: [...form.schools, emptySchool()],
});

const removeSchool = (form: SchoolInfoForm, id: string): SchoolInfoForm => ({
  ...form,
  schools: form.schools.filter((school) => school.id !== id),
});

const confirmSchoolName = (form: SchoolInfoForm, id: string): SchoolInfoForm => {
  const school = form.schools.find((row) => row.id === id);
  const name = school?.name.trim() ?? '';
  if (!name) return form;
  return updateSchool(form, id, { name, nameConfirmed: true });
};

const updateSchool = (form: SchoolInfoForm, id: string, patch: Partial<SchoolInfoRow>): SchoolInfoForm => ({
  ...form,
  schools: form.schools.map((school) => {
    if (school.id !== id) return school;

    const nextSchool = { ...school, ...patch };
    if (patch.hasCteCourses === 'no') {
      return {
        ...nextSchool,
        hasDetailedCteClusters: '',
        highWageThreshold: '',
        highGrowthThreshold: '',
      };
    }

    if (patch.hasDetailedCteClusters === 'no') {
      return { ...nextSchool, highWageThreshold: '', highGrowthThreshold: '' };
    }

    return nextSchool;
  }),
});

const toSchoolWriteBody = (school: SchoolInfoRow): SchoolInfoWriteBody => {
  const body: SchoolInfoWriteBody = {};
  const name = school.name.trim();
  if (name) body.name = name;

  const schoolType = school.schoolType.trim();
  if (schoolType) body.type = schoolType;

  const schoolNumber = school.schoolNumber.trim();
  if (schoolNumber) body.src_school_id = schoolNumber;

  const isPrimary = booleanFromYesNo(school.isPrimary);
  if (isPrimary !== undefined) body.is_primary_on_eoa = isPrimary;

  const requiresSat = booleanFromYesNo(school.requiresSat);
  if (requiresSat !== undefined) body.required_sat_for_graduation = requiresSat;

  const requiresAct = booleanFromYesNo(school.requiresAct);
  if (requiresAct !== undefined) body.required_act_for_graduation = requiresAct;

  const graduationStatus = booleanFromYesNo(school.graduationStatus);
  if (graduationStatus !== undefined) body.can_provide_graduation_status = graduationStatus;

  const hasCte = booleanFromYesNo(school.hasCteCourses);
  if (hasCte !== undefined) body.has_cte_courses = hasCte;

  if (showsDetailedCte(school)) {
    const detailedCte = booleanFromYesNo(school.hasDetailedCteClusters);
    if (detailedCte !== undefined) body.has_detailed_cte_clusters = detailedCte;
  }

  if (showsWageThresholds(school)) {
    const highWage = parseOptionalNumber(school.highWageThreshold);
    if (highWage !== undefined) body.career_high_wage_threshold = highWage;
    const highGrowth = parseOptionalNumber(school.highGrowthThreshold);
    if (highGrowth !== undefined) body.career_high_growth_threshold = highGrowth;
  }

  if (school.regionalAreaType) body.regional_area_type = school.regionalAreaType;
  const regionalTitle = school.regionalAreaTitle.trim();
  if (regionalTitle) body.regional_area_title = regionalTitle;

  return body;
};

const comparableWriteBody = (body: SchoolInfoWriteBody): string => JSON.stringify(body);

const changedSchoolPayload = (previous: SchoolInfoWriteBody, next: SchoolInfoWriteBody): SchoolInfoPatchBody => {
  const payload: SchoolInfoPatchBody = {};
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  (Array.from(keys) as (keyof SchoolInfoWriteBody)[]).forEach((key) => {
    const value = next[key] ?? null;
    if (JSON.stringify(previous[key]) === JSON.stringify(value)) return;
    Object.assign(payload, { [key]: value });
  });
  return payload;
};

const extractSchoolId = (data: unknown): string => {
  if (typeof data === 'string' && data.length > 0) return data;
  if (data && typeof data === 'object') {
    if ('data' in data) return extractSchoolId((data as { data: unknown }).data);
    if ('id' in data && typeof (data as { id: unknown }).id === 'string' && (data as { id: string }).id.length > 0) {
      return (data as { id: string }).id;
    }
    if (
      'school_id' in data &&
      typeof (data as { school_id: unknown }).school_id === 'string' &&
      (data as { school_id: string }).school_id.length > 0
    ) {
      return (data as { school_id: string }).school_id;
    }
  }
  throw new Error('School create response did not include an id');
};

const schoolFromSources = (configured: ConfiguredEOASchool, config: SchoolConfigEntry | undefined): SchoolInfoRow => ({
  ...emptySchool(),
  savedId: configured.id,
  name: configured.name ?? config?.school_name ?? '',
  nameConfirmed: true,
  isPrimary: yesNoFromBoolean(config?.is_primary_on_eoa ?? configured.is_primary_on_eoa),
  schoolNumber: configured.src_school_id ?? configured.school_identifier ?? '',
  schoolType: configured.type ?? configured.school_type ?? '',
  graduationStatus: yesNoFromBoolean(config?.can_provide_graduation_status ?? configured.can_provide_graduation_status),
  requiresSat: yesNoFromBoolean(config?.required_sat_for_graduation),
  requiresAct: yesNoFromBoolean(config?.required_act_for_graduation),
  hasCteCourses: yesNoFromBoolean(config?.has_cte_courses),
  hasDetailedCteClusters: yesNoFromBoolean(config?.has_detailed_cte_clusters),
  highWageThreshold: numberToField(config?.career_high_wage_threshold),
  highGrowthThreshold: numberToField(config?.career_high_growth_threshold),
  regionalAreaType: config?.regional_area_type ?? '',
  regionalAreaTitle: config?.regional_area_title ?? '',
});

const schoolInfoFromSources = (configured: ConfiguredEOASchool[], configs: SchoolConfigEntry[]): SchoolInfoForm => {
  if (configured.length === 0) return INITIAL_SCHOOL_INFO_FORM;
  const configById = new Map(configs.map((entry) => [entry.school_id, entry]));
  return {
    schools: configured.map((school) => schoolFromSources(school, configById.get(school.id))),
  };
};

const persistSchoolInfo = async ({
  form,
  previousForm,
  eoaId,
  createSchool,
  patchSchool,
  deleteSchool,
  createDraft,
}: {
  form: SchoolInfoForm;
  previousForm: SchoolInfoForm;
  eoaId: string | null;
  createSchool: (eoaId: string, payload: SchoolInfoWriteBody) => Promise<unknown>;
  patchSchool: (eoaId: string, schoolId: string, payload: SchoolInfoPatchBody) => Promise<unknown>;
  deleteSchool: (eoaId: string, schoolId: string) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<SchoolInfoForm> => {
  if (hasInvalidSchoolInfo(form)) return form;

  const hasContent = form.schools.some(hasSchoolContent);
  const draftId = await ensureDraftId({ eoaId, hasContent, createDraft });
  if (!draftId) return form;

  const previousById = new Map(previousForm.schools.map((school) => [school.id, school]));
  const nextIds = new Set(form.schools.map((school) => school.id));
  const schools: SchoolInfoRow[] = [];

  for (const previous of previousForm.schools) {
    if (previous.savedId && !nextIds.has(previous.id)) {
      await deleteSchool(draftId, previous.savedId);
    }
  }

  for (const school of form.schools) {
    if (!hasSchoolContent(school)) {
      if (school.savedId) await deleteSchool(draftId, school.savedId);
      schools.push({ ...school, savedId: null });
      continue;
    }

    const nextBody = toSchoolWriteBody(school);
    if (!school.savedId) {
      schools.push({ ...school, savedId: extractSchoolId(await createSchool(draftId, nextBody)) });
      continue;
    }

    const previous = previousById.get(school.id);
    const previousBody = previous ? toSchoolWriteBody(previous) : {};
    if (comparableWriteBody(previousBody) !== comparableWriteBody(nextBody)) {
      const payload = changedSchoolPayload(previousBody, nextBody);
      if (Object.keys(payload).length > 0) await patchSchool(draftId, school.savedId, payload);
    }
    schools.push(school);
  }

  return { schools };
};

export {
  INITIAL_SCHOOL_INFO_FORM,
  REGIONAL_AREA_OPTIONS,
  addSchool,
  confirmSchoolName,
  hasInvalidSchoolInfo,
  hasSchoolContent,
  persistSchoolInfo,
  removeSchool,
  schoolInfoFromSources,
  schoolNameError,
  showsDetailedCte,
  showsWageThresholds,
  toSchoolWriteBody,
  updateSchool,
};
